import type {
  PlanogramEntityKind,
  PlanogramEntityRef,
  PlanogramScene,
  PlanogramSelection,
  PlanogramWorldRect,
} from "./types";

export interface PlanogramSelectionModifiers {
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly shiftKey?: boolean;
}

export interface PlanogramWorldPoint {
  readonly x: number;
  readonly y: number;
}

interface WorldEdges {
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
}

const getWorldEdges = (rect: PlanogramWorldRect): WorldEdges => ({
  bottom: Math.max(rect.y, rect.y + rect.height),
  left: Math.min(rect.x, rect.x + rect.width),
  right: Math.max(rect.x, rect.x + rect.width),
  top: Math.min(rect.y, rect.y + rect.height),
});

const containsPoint = (rect: PlanogramWorldRect, point: PlanogramWorldPoint) => {
  const edges = getWorldEdges(rect);

  return (
    point.x >= edges.left &&
    point.x <= edges.right &&
    point.y >= edges.top &&
    point.y <= edges.bottom
  );
};

const getCanonicalShelfIds = (scene: PlanogramScene): readonly string[] => {
  const bayOrder = new Map(scene.bays.map((bay, index) => [bay.id, index]));
  const entities = [
    ...scene.shelves.map((shelf) => ({ id: shelf.id, bayId: shelf.bayId, shelfNo: shelf.shelfNo })),
    ...scene.fixtures.map((fixture) => ({
      id: fixture.id,
      bayId: fixture.bayId,
      shelfNo: fixture.shelfNo,
    })),
  ];
  return entities
    .sort(
      (first, second) =>
        (bayOrder.get(first.bayId) ?? Number.MAX_SAFE_INTEGER) -
          (bayOrder.get(second.bayId) ?? Number.MAX_SAFE_INTEGER) || first.shelfNo - second.shelfNo,
    )
    .map((entity) => entity.id);
};

const getCanonicalEntityIds = (
  scene: PlanogramScene,
  kind: PlanogramEntityKind,
): readonly string[] => {
  switch (kind) {
    case "bay":
      return scene.bays.map((bay) => bay.id);
    case "shelf":
      return getCanonicalShelfIds(scene);
    case "container":
      return scene.containers.map((container) => container.id);
    case "product":
      return scene.products.map((product) => product.id);
    case "pegboard":
      return (scene.pegboards ?? [])
        .filter(({ source }) => source === "planogram")
        .map((pegboard) => pegboard.id);
    case "placement":
      return (scene.placements ?? []).map((placement) => placement.id);
  }
};

const orderSelectedIds = (canonicalIds: readonly string[], selectedIds: ReadonlySet<string>) => {
  const orderedIds: string[] = [];
  const seenIds = new Set<string>();

  for (const id of canonicalIds) {
    if (selectedIds.has(id) && !seenIds.has(id)) {
      orderedIds.push(id);
      seenIds.add(id);
    }
  }

  return orderedIds;
};

export const clearSelection = (): PlanogramSelection => ({
  anchorId: null,
  focusedId: null,
  ids: [],
  kind: null,
});

export const reconcileSelection = (
  selection: PlanogramSelection,
  scene: PlanogramScene,
): PlanogramSelection => {
  if (selection.kind === null) return clearSelection();

  const canonicalIds = getCanonicalEntityIds(scene, selection.kind);
  const validIds = new Set(canonicalIds);
  const selectedIds = orderSelectedIds(canonicalIds, new Set(selection.ids));

  if (selectedIds.length === 0) {
    if (
      selection.ids.length === 0 &&
      selection.focusedId !== null &&
      validIds.has(selection.focusedId)
    ) {
      return {
        anchorId: null,
        focusedId: selection.focusedId,
        ids: [],
        kind: selection.kind,
      };
    }

    return clearSelection();
  }
  const selectedIdSet = new Set(selectedIds);

  return {
    anchorId:
      selection.anchorId !== null && selectedIdSet.has(selection.anchorId)
        ? selection.anchorId
        : null,
    focusedId:
      selection.focusedId !== null && validIds.has(selection.focusedId)
        ? selection.focusedId
        : null,
    ids: selectedIds,
    kind: selection.kind,
  };
};

export const areSelectionsEqual = (first: PlanogramSelection, second: PlanogramSelection) =>
  first.kind === second.kind &&
  first.anchorId === second.anchorId &&
  first.focusedId === second.focusedId &&
  first.ids.length === second.ids.length &&
  first.ids.every((id, index) => id === second.ids[index]);

const replaceWithProduct = (productId: string): PlanogramSelection => ({
  anchorId: productId,
  focusedId: productId,
  ids: [productId],
  kind: "product",
});

const replaceWithShelf = (shelfId: string): PlanogramSelection => ({
  anchorId: shelfId,
  focusedId: shelfId,
  ids: [shelfId],
  kind: "shelf",
});

export const selectShelf = (
  scene: PlanogramScene,
  selection: PlanogramSelection,
  shelfId: string,
  modifiers: PlanogramSelectionModifiers = {},
): PlanogramSelection => {
  const shelf =
    scene.shelves.find((candidate) => candidate.id === shelfId) ??
    scene.fixtures.find((candidate) => candidate.id === shelfId);
  if (!shelf) return reconcileSelection(selection, scene);
  const currentSelection = reconcileSelection(selection, scene);
  const groupIds =
    "groupId" in shelf
      ? getCanonicalShelfIds(scene).filter((id) =>
          scene.shelves.some(
            (candidate) => candidate.id === id && candidate.groupId === shelf.groupId,
          ),
        )
      : [shelf.id];
  const isShelfSelection = currentSelection.kind === "shelf";
  const shouldToggle = Boolean(modifiers.ctrlKey || modifiers.metaKey);

  if (!isShelfSelection || !shouldToggle) {
    return { ...replaceWithShelf(shelfId), ids: groupIds };
  }

  const selectedIds = new Set(currentSelection.ids);
  for (const id of groupIds) {
    if (selectedIds.has(id)) selectedIds.delete(id);
    else selectedIds.add(id);
  }
  const ids = orderSelectedIds(getCanonicalShelfIds(scene), selectedIds);
  if (ids.length === 0) return clearSelection();
  return {
    anchorId: ids.includes(currentSelection.anchorId ?? "")
      ? currentSelection.anchorId
      : (ids[0] ?? null),
    focusedId: shelfId,
    ids,
    kind: "shelf",
  };
};

export const selectProduct = (
  scene: PlanogramScene,
  selection: PlanogramSelection,
  productId: string,
  modifiers: PlanogramSelectionModifiers = {},
): PlanogramSelection => {
  const clickedProduct = scene.products.find((product) => product.id === productId);
  if (!clickedProduct) return reconcileSelection(selection, scene);

  const currentSelection = reconcileSelection(selection, scene);
  const isProductSelection = currentSelection.kind === "product";
  const shouldToggle = Boolean(modifiers.ctrlKey || modifiers.metaKey);

  if (!isProductSelection || !shouldToggle) {
    return replaceWithProduct(productId);
  }

  const selectedIds = new Set(currentSelection.ids);
  const wasSelected = selectedIds.delete(productId);
  if (!wasSelected) selectedIds.add(productId);

  const ids = orderSelectedIds(
    scene.products.map((product) => product.id),
    selectedIds,
  );
  if (ids.length === 0) return clearSelection();

  return {
    anchorId:
      currentSelection.anchorId !== null && selectedIds.has(currentSelection.anchorId)
        ? currentSelection.anchorId
        : ids[0],
    focusedId: productId,
    ids,
    kind: "product",
  };
};

const replaceWithEntity = (
  kind: "container" | "pegboard" | "placement",
  id: string,
): PlanogramSelection => ({
  anchorId: id,
  focusedId: id,
  ids: [id],
  kind,
});

export const selectContainer = (
  scene: PlanogramScene,
  selection: PlanogramSelection,
  containerId: string,
  modifiers: PlanogramSelectionModifiers = {},
): PlanogramSelection => selectEntity(scene, selection, "container", containerId, modifiers);

export const selectPegboard = (
  scene: PlanogramScene,
  selection: PlanogramSelection,
  pegboardId: string,
  modifiers: PlanogramSelectionModifiers = {},
): PlanogramSelection => selectEntity(scene, selection, "pegboard", pegboardId, modifiers);

export const selectPlacement = (
  scene: PlanogramScene,
  selection: PlanogramSelection,
  placementId: string,
  modifiers: PlanogramSelectionModifiers = {},
): PlanogramSelection => selectEntity(scene, selection, "placement", placementId, modifiers);

const selectEntity = (
  scene: PlanogramScene,
  selection: PlanogramSelection,
  kind: "container" | "pegboard" | "placement",
  id: string,
  modifiers: PlanogramSelectionModifiers,
): PlanogramSelection => {
  const ids = getCanonicalEntityIds(scene, kind);
  if (!ids.includes(id)) return reconcileSelection(selection, scene);
  const current = reconcileSelection(selection, scene);
  if (current.kind !== kind || (!modifiers.ctrlKey && !modifiers.metaKey)) {
    return replaceWithEntity(kind, id);
  }
  const selected = new Set(current.ids);
  if (selected.has(id)) selected.delete(id);
  else selected.add(id);
  const ordered = orderSelectedIds(ids, selected);
  return ordered.length === 0
    ? clearSelection()
    : {
        anchorId: ordered.includes(current.anchorId ?? "")
          ? current.anchorId
          : (ordered[0] ?? null),
        focusedId: id,
        ids: ordered,
        kind,
      };
};

export const focusProduct = (
  scene: PlanogramScene,
  selection: PlanogramSelection,
  productId: string,
): PlanogramSelection => focusEntity(scene, selection, "product", productId);

/** Focus an entity without changing its current selection, like roving focus for DOM controls. */
export const focusEntity = (
  scene: PlanogramScene,
  selection: PlanogramSelection,
  kind: PlanogramEntityKind,
  id: string,
): PlanogramSelection => {
  if (!getCanonicalEntityIds(scene, kind).includes(id)) {
    return reconcileSelection(selection, scene);
  }

  const currentSelection = reconcileSelection(selection, scene);
  return {
    anchorId: currentSelection.kind === kind ? currentSelection.anchorId : null,
    focusedId: id,
    ids: currentSelection.kind === kind ? currentSelection.ids : [],
    kind,
  };
};

export const hitTestFixture = (
  scene: PlanogramScene,
  worldPoint: PlanogramWorldPoint,
): PlanogramEntityRef | null => {
  for (let index = scene.fixtures.length - 1; index >= 0; index -= 1) {
    const fixture = scene.fixtures[index];
    if (!fixture) continue;
    const isHit =
      fixture.fixtureType === "HANGCELL"
        ? worldPoint.x >= fixture.x &&
          worldPoint.x <= fixture.x + fixture.width &&
          worldPoint.y >= fixture.y - fixture.thickness &&
          worldPoint.y <= fixture.y
        : containsPoint(
            { ...fixture, width: fixture.boardWidth, x: fixture.x + fixture.boardX },
            worldPoint,
          );
    if (isHit) return { id: fixture.id, kind: "shelf" };
  }

  return null;
};

export const hitTestPegboard = (
  scene: PlanogramScene,
  worldPoint: PlanogramWorldPoint,
): PlanogramEntityRef | null => {
  const pegboards = scene.pegboards ?? [];
  for (let index = pegboards.length - 1; index >= 0; index -= 1) {
    const pegboard = pegboards[index];
    if (pegboard?.source === "planogram" && containsPoint(pegboard, worldPoint)) {
      return { id: pegboard.id, kind: "pegboard" };
    }
  }
  return null;
};

export const hitTestPlacement = (
  scene: PlanogramScene,
  worldPoint: PlanogramWorldPoint,
): PlanogramEntityRef | null => {
  const placements = scene.placements ?? [];
  for (let index = placements.length - 1; index >= 0; index -= 1) {
    const placement = placements[index];
    if (placement && containsPoint(placement, worldPoint))
      return { id: placement.id, kind: "placement" };
  }
  return null;
};

export const PEGBOARD_HOLE_SNAP_TOLERANCE_PX = 16;

export const getNearestPegboardHole = (
  scene: PlanogramScene,
  worldPoint: PlanogramWorldPoint,
  maxDistance = Number.POSITIVE_INFINITY,
): { pegboardId: string; holeNoLeft: number; holeNoTop: number } | null => {
  const pegboards = scene.pegboards ?? [];

  // Scene order is paint order, so walking backwards gives the topmost
  // eligible board when boards overlap.
  for (let index = pegboards.length - 1; index >= 0; index -= 1) {
    const pegboard = pegboards[index];
    if (pegboard?.source !== "planogram" || !containsPoint(pegboard, worldPoint)) continue;
    let nearestHole: (typeof pegboard.holes)[number] | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    for (const hole of pegboard.holes) {
      const distance = Math.hypot(
        worldPoint.x - (hole.x + hole.width / 2),
        worldPoint.y - (hole.y + hole.height / 2),
      );
      if (distance < nearestDistance) {
        nearestHole = hole;
        nearestDistance = distance;
      }
    }

    if (!nearestHole || nearestDistance > maxDistance) continue;

    return {
      pegboardId: pegboard.id,
      holeNoLeft: nearestHole.column + 1,
      holeNoTop: nearestHole.row + 1,
    };
  }

  return null;
};

/**
 * Hit-tests the merchandisable row ABOVE a regular shelf board (the region the
 * DOM shelf Dropper covers): from the bottom edge of the fixture above down to
 * the board itself. Lets external drops land on empty shelf space.
 */
export const hitTestShelfRow = (
  scene: PlanogramScene,
  worldPoint: PlanogramWorldPoint,
): PlanogramEntityRef | null => {
  const rowCeilings = [...scene.shelves, ...scene.fixtures];

  for (let index = scene.shelves.length - 1; index >= 0; index -= 1) {
    const shelf = scene.shelves[index];
    if (!shelf) continue;

    // A board is its drawn (live) part; a board above bounds the row only over its own part.
    const boardLeft = shelf.x + shelf.boardX;
    const boardRight = boardLeft + shelf.boardWidth;
    if (worldPoint.x < boardLeft || worldPoint.x > boardRight) continue;

    const bay = scene.bays.find((candidate) => candidate.id === shelf.bayId);
    const rowTop = rowCeilings.reduce(
      (nearest, candidate) =>
        candidate.bayId === shelf.bayId &&
        candidate.id !== shelf.id &&
        candidate.y + candidate.height <= shelf.y &&
        worldPoint.x >= candidate.x + candidate.boardX &&
        worldPoint.x <= candidate.x + candidate.boardX + candidate.boardWidth
          ? Math.max(nearest, candidate.y + candidate.height)
          : nearest,
      bay?.y ?? 0,
    );

    if (worldPoint.y >= rowTop && worldPoint.y <= shelf.y + shelf.height) {
      return { id: shelf.id, kind: "shelf" };
    }
  }

  return null;
};

export const hitTestContainer = (
  scene: PlanogramScene,
  worldPoint: PlanogramWorldPoint,
): PlanogramEntityRef | null => {
  for (let index = scene.containers.length - 1; index >= 0; index -= 1) {
    const container = scene.containers[index];
    if (container && containsPoint(container, worldPoint)) {
      return { id: container.id, kind: "container" };
    }
  }

  return null;
};

export const hitTestProduct = (
  scene: PlanogramScene,
  worldPoint: PlanogramWorldPoint,
  excludedProductIds: ReadonlySet<string> = new Set(),
): PlanogramEntityRef | null => {
  for (let index = scene.products.length - 1; index >= 0; index -= 1) {
    const product = scene.products[index];
    if (product && !excludedProductIds.has(product.id) && containsPoint(product, worldPoint)) {
      return { id: product.id, kind: "product" };
    }
  }

  return null;
};
